/**
 * YouTube links in the text of a post (`docs/spec.md` «Каталог и данные»).
 *
 * A post whose text carries a YouTube link becomes a catalogue record of kind `youtube`,
 * and the identifier this module extracts is that record's DEDUPLICATION KEY: the bot
 * creates the row the moment the owner forwards the link, the indexer meets the same post
 * later, and the two must agree byte for byte or the catalogue grows a twin. That is why
 * everything below is written as pure functions with no state and no clock — the same
 * input has to give the same identifier in the bot, in the indexer and in a test.
 *
 * ## The accepted forms are a CLOSED list
 *
 * The checklist of task 02 says «любые другие ссылки — ничего», so exactly three paths
 * yield an identifier:
 *
 *   - `youtube.com/watch?v=<id>`  — the desktop share form
 *   - `youtu.be/<id>`             — the mobile share form
 *   - `youtube.com/shorts/<id>`   — a short
 *
 * `/embed/`, `/live/`, `/v/`, `/playlist`, `/channel/`, `/@handle` and `/results` yield
 * nothing even though a perfectly good identifier is visible inside some of them.
 * `/embed/` in particular is an OUTPUT of this application (the player frames
 * `youtube-nocookie.com/embed/…`), never an input: the owner does not post embed links.
 *
 * ## The host is matched against an exact list, never a suffix
 *
 * `host.endsWith('youtube.com')` accepts `notyoutube.com` and `host.includes('youtube.com')`
 * accepts `youtube.com.evil.ru`. Both are real phishing shapes and both would put a
 * stranger's video in the owner's catalogue, so the host is compared against
 * {@link YOUTUBE_HOSTS} — a set of complete host names. The four spellings of the site
 * (bare, `www.`, `m.`, `music.`) are all in the set: they are the same address written by
 * a desktop, a phone and the music app, and refusing one of them means a post that
 * silently never appears — the exact puzzle the owner hit on the old project.
 *
 * ## `URL`, not a regular expression, for the query string
 *
 * `https://www.youtube.com/watch?t=30&v=<id>` is a real form (the share dialog emits it
 * when a timestamp is set), and a `v=([^&]+)` pattern anchored at the start of the query
 * misses it. `URLSearchParams` does not care about order, so the query is parsed rather
 * than matched. The host is taken from the same parser for the same reason.
 */
/**
 * The complete host names that are YouTube. A set, not a suffix test — see the header.
 * Adding a host here is the entire cost of accepting a new spelling of the same site.
 */
const YOUTUBE_HOSTS = new Set([
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'music.youtube.com',
]);
/** The short form. Kept apart because its path shape is different: the id IS the path. */
const YOUTU_BE_HOSTS = new Set(['youtu.be', 'www.youtu.be']);
/**
 * If YouTube ever lengthens the identifier, change it HERE. Eleven characters is
 * base64url of a 64-bit number without padding; a playlist (`PL…`, 13–34 characters), a
 * channel (`UC…`, 24) and a handle (`@name`) never fit it, which is why the length check
 * alone rejects `/playlist?list=…` even when the parser is fed it directly.
 */
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
/** `/watch` and `/watch/` — the keyword is forgiven its case, the identifier never is. */
const WATCH_PATH_RE = /^\/watch\/?$/iu;
/** `/shorts/<segment>`, with an optional trailing slash. */
const SHORTS_PATH_RE = /^\/shorts\/([^/]+)\/?$/iu;
/** `/<segment>` — the whole path of a `youtu.be` address is the identifier. */
const SHORT_HOST_PATH_RE = /^\/([^/]+)\/?$/u;
/** `https://` or `http://`, in any case. */
const HTTP_SCHEME_RE = /^https?:\/\//iu;
/** Any scheme at all: `ftp:`, `javascript:`, and also `youtube.com:443` — see below. */
const ANY_SCHEME_RE = /^[a-z][a-z0-9+.-]*:/iu;
/** A schemeless address has to start with the host, i.e. with a letter or a digit. */
const HOST_START_RE = /^[a-z0-9]/iu;
/*
 * ── Where a link BEGINS and where it ENDS ──────────────────────────────────────
 *
 * The owner posts from a phone and puts an emoji, an em dash or a comma right up against
 * the address: `https://youtu.be/<id>🔥` is an ordinary evening, not an edge case. A link
 * body that runs «до пробела» swallows the emoji, the address stops parsing, and the post
 * never reaches the catalogue at all. So the END of a link is the identifier's own shape —
 * exactly eleven characters of `[A-Za-z0-9_-]` — and not the next space.
 *
 * The BEGINNING is guarded by a lookbehind, because a redirector carries a whole YouTube
 * address inside its query (`vk.com/away.php?to=youtube.com/watch?v=<id>`). That text
 * really does contain `youtube.com/watch?v=…`, and it is still somebody ELSE'S link: `=`,
 * `?`, `&`, `#` and `%` in front of the host mean «внутри чужого адреса», exactly as a
 * letter in front of it means `notyoutube.com`.
 */
/** The identifier as it is written inside a text: eleven characters of its own alphabet. */
const ID_BODY = String.raw `[A-Za-z0-9_-]{11}`;
/**
 * After the identifier the address has to be OVER. A twelfth character of the alphabet, a
 * percent escape and a further path segment each mean the text carries a LONGER address
 * than a link to a video, and cutting it back to eleven characters would invent a video
 * the owner never posted. Everything else — a full stop, a comma, an emoji, a Cyrillic
 * word — is the sentence around the link, and simply ends it.
 */
const ID_END = String.raw `(?![A-Za-z0-9_-]|%|\/\S)`;
/**
 * What may sit in front of `https://`. A letter or a digit glues the scheme into a longer
 * word; `=`, `&`, `?`, `#` and `%` mean this address is a parameter of another one. A
 * colon is deliberately NOT here: `Смотри:https://youtu.be/…` is written without a space
 * every day.
 */
const SCHEME_START = String.raw `(?<![\p{L}\p{N}=&?#%+~-])https?:\/\/`;
/**
 * What may sit in front of a SCHEMELESS `youtube.com/…` — the form Telegram makes
 * clickable and the owner therefore writes. Everything above, plus `.`, `:`, `/` and `@`:
 * those four are what tell `notyoutube.com/…`, `foo.youtube.com/…`, `ftp://youtube.com/…`,
 * `//youtu.be/…` and `fake.com#youtu.be/…` from a link somebody meant to write.
 */
const BARE_START = String.raw `(?<![\p{L}\p{N}@._:\/=&?#%+~-])`;
/**
 * A YouTube link inside free text, in the three accepted shapes and in nothing else.
 *
 * The query prefix of the `/watch` shape stops at `?` and `#` as well as at whitespace, so
 * the hunt for `&v=` can never run out of one address into the next: that is what keeps a
 * post full of links linear instead of quadratic.
 */
const YOUTUBE_LINK_SOURCE = `(?:${SCHEME_START}|${BARE_START})(?:` +
    String.raw `(?:www\.)?youtu\.be\/${ID_BODY}` +
    '|' +
    String.raw `(?:www\.|m\.|music\.)?youtube\.com\/` +
    String.raw `(?:shorts\/${ID_BODY}|watch\/?\?(?:[^\s#?"'«»<>]*&)?v=${ID_BODY})` +
    `)${ID_END}`;
/** Every YouTube link of a text, in reading order. */
const YOUTUBE_LINK_RE = new RegExp(YOUTUBE_LINK_SOURCE, 'giu');
/** The same shape anchored at the front, for the ONE address {@link parseYoutubeId} gets. */
const YOUTUBE_LINK_HEAD_RE = new RegExp(`^(?:${YOUTUBE_LINK_SOURCE})`, 'iu');
/**
 * Addresses of ANY site inside free text, for {@link stripUrls} alone. Two shapes:
 * anything introduced by a scheme or by `www.` — an ordinary `https://example.com/#anchor`
 * would otherwise donate a hashtag called `anchor` to the caption parser — and the bare
 * `youtube.com/…` / `youtu.be/…` forms.
 *
 * This one keeps a greedy body on purpose: for CUTTING, one character too many is safe and
 * one too few is not. Only the tail that no address could contain is handed back to the
 * text afterwards — see {@link NON_URL_TAIL_RE}.
 */
const URL_CANDIDATE_RE = new RegExp(String.raw `(?:https?:\/\/|www\.)[^\s<>"'«»]+` +
    `|${BARE_START}` +
    String.raw `(?:m\.|music\.)?(?:youtube\.com|youtu\.be)\/[^\s<>"'«»]*`, 'giu');
/**
 * The run at the END of a candidate that no address can contain: an emoji, a Cyrillic
 * word, an em dash. {@link stripUrls} puts it back, because it is the owner's text and the
 * fallback title is read from what is left — `Клип https://youtu.be/<id>🔥` keeps its 🔥.
 * Only a TRAILING run comes back: a non-ASCII character in the MIDDLE of an address
 * (`example.com/путь#anchor`) is part of that address and goes with it, or the `#anchor`
 * behind it would become a tag.
 */
const NON_URL_TAIL_RE = /[^A-Za-z0-9._~:/?#[\]@!$&()*+,;=%-]+$/u;
/** The one place the canonical spelling of a YouTube address is written down. */
const CANONICAL_WATCH_PREFIX = 'https://www.youtube.com/watch?v=';
/** Shared empty result, so «нет ссылок» costs no allocation on the indexer's hot path. */
const NO_LINKS = Object.freeze([]);
/**
 * Put a scheme in front of a schemeless address, or refuse the string outright.
 *
 * Returns `undefined` for everything that is not an `http(s)` address written from the
 * host onwards: `ftp://…` and `javascript:…` (a scheme we do not serve), `//youtu.be/…`
 * (protocol-relative — a browser resolves it, a post does not), and `youtube.com:443/…`
 * (a port reads as a scheme here, and a link with a port is not a link the owner wrote).
 */
function withHttpScheme(address) {
    if (HTTP_SCHEME_RE.test(address))
        return address;
    if (ANY_SCHEME_RE.test(address))
        return undefined;
    if (!HOST_START_RE.test(address))
        return undefined;
    return `https://${address}`;
}
/**
 * Read the identifier out of a single address.
 *
 * `undefined` means «это не ссылка на ролик» — a different host, a path outside the closed
 * list, or an identifier of the wrong shape. The caller (the indexer's link pass) is
 * expected to log that refusal VISIBLY: «ссылка не проиндексировалась» must never be a
 * mystery.
 *
 * A percent-encoded path segment is not decoded: the identifier alphabet has no character
 * that needs encoding, so an address that contains `%` is not an address to a video.
 */
export function parseYoutubeId(url) {
    const trimmed = url.trim();
    // The identifier's own shape says where the link ends, so a character glued onto it —
    // `https://youtu.be/<id>🔥` off a phone keyboard — is cut away BEFORE the address parser
    // ever sees it. When nothing matches, the whole string goes through unchanged and is
    // refused below: this step only ever NARROWS a link, it never invents one.
    const head = YOUTUBE_LINK_HEAD_RE.exec(trimmed);
    const address = withHttpScheme(head?.[0] ?? trimmed);
    if (address === undefined)
        return undefined;
    // `withHttpScheme` is the ONLY scheme gate: whatever reaches the parser here starts with
    // `http://` or `https://`, so the protocol needs no second check.
    let parsed;
    try {
        parsed = new URL(address);
    }
    catch {
        // A malformed address is data, not an incident: the owner pasted something odd
        // (`https://`, `https://[`). Nothing to index, nothing to raise.
        return undefined;
    }
    const host = parsed.hostname.toLowerCase();
    if (YOUTU_BE_HOSTS.has(host)) {
        const segment = SHORT_HOST_PATH_RE.exec(parsed.pathname);
        return validId(segment?.[1]);
    }
    if (!YOUTUBE_HOSTS.has(host))
        return undefined;
    if (WATCH_PATH_RE.test(parsed.pathname)) {
        return validId(parsed.searchParams.get('v'));
    }
    const shorts = SHORTS_PATH_RE.exec(parsed.pathname);
    if (shorts !== null)
        return validId(shorts[1]);
    return undefined;
}
/** The single gate every candidate identifier passes through. */
function validId(candidate) {
    return isYoutubeId(candidate) ? candidate : undefined;
}
/**
 * Every video in a free text, without repeats, in the order the links appear.
 *
 * Two spellings of one video (`youtu.be/<id>` and `watch?v=<id>`) collapse into a single
 * entry, and the entry keeps the FIRST spelling as its `raw`, because that is the one the
 * owner actually wrote.
 */
export function findYoutubeLinks(text) {
    if (text === null || text === undefined || text === '')
        return NO_LINKS;
    const seen = new Set();
    const links = [];
    for (const match of text.matchAll(YOUTUBE_LINK_RE)) {
        const raw = match[0];
        // The scanner says WHERE a link is; `parseYoutubeId` says WHAT it is. One authority
        // for the identifier is what keeps the bot and the indexer from disagreeing about a
        // post — `watch?v=<a>&v=<b>` has to give the same answer to both of them.
        const id = parseYoutubeId(raw);
        if (id === undefined || seen.has(id))
            continue;
        seen.add(id);
        links.push(Object.freeze({ id, url: youtubeWatchUrl(id), raw }));
    }
    return Object.freeze(links);
}
/** `/live/<id>` — how YouTube shares a stream; `/playlist` — a list, not a clip. */
const STREAM_PATH_RE = /^\/live\//iu;
const PLAYLIST_PATH_RE = /^\/playlist\/?$/iu;
/**
 * Task 26: the first YouTube address in a text that names something OTHER than one clip — a
 * stream (`'stream'`) or a playlist (`'playlist'`); `undefined` when there is none.
 *
 * {@link parseYoutubeId} refuses both, silently and rightly: neither is a video to index. The
 * bot needs the difference, because a link it does not take must still get an answer, and
 * «это эфир» is a different answer from «это плейлист». The hosts are the same sets the parser
 * reads, so a spelling the parser learns is a spelling this learns too.
 */
export function findYoutubeNonClip(text) {
    if (text === null || text === undefined || text === '')
        return undefined;
    for (const match of text.matchAll(URL_CANDIDATE_RE)) {
        const address = withHttpScheme(match[0].replace(NON_URL_TAIL_RE, ''));
        if (address === undefined)
            continue;
        let parsed;
        try {
            parsed = new URL(address);
        }
        catch {
            continue;
        }
        const host = parsed.hostname.toLowerCase();
        if (!YOUTUBE_HOSTS.has(host) && !YOUTU_BE_HOSTS.has(host))
            continue;
        if (STREAM_PATH_RE.test(parsed.pathname))
            return 'stream';
        if (PLAYLIST_PATH_RE.test(parsed.pathname))
            return 'playlist';
    }
    return undefined;
}
/**
 * The first video in a free text. One post is one record (`docs/spec.md` «Каталог и
 * данные»), so this — not {@link findYoutubeLinks} — is what the indexer and the bot call.
 */
export function findYoutubeLink(text) {
    return findYoutubeLinks(text)[0];
}
/**
 * The text with every address replaced by a single space.
 *
 * Two callers need it and neither can be written without it. The hashtag scan in
 * `caption.ts` would otherwise read `https://example.com/#anchor` as a tag called
 * `anchor`; and the fallback title of a YouTube post is defined by `docs/spec.md` as
 * «первая строка текста БЕЗ ссылки», which is this function followed by "take the first
 * non-empty line".
 *
 * A space rather than nothing, so that removing an address cannot glue two words
 * together; line breaks are preserved, because the line structure is what the fallback
 * title is read from.
 */
export function stripUrls(text) {
    if (text === null || text === undefined || text === '')
        return '';
    return text.replace(URL_CANDIDATE_RE, cutAddress);
}
/**
 * Whether a post's text is a bare link to `youtubeId` and nothing else: no words, no second
 * video — the 93 old clips of the channel, as the owner posted them (`watch?v=<id>&list=RD…`).
 *
 * The move of the old clips (tasks 29–31) replaces only such posts, and in the end DELETES
 * them: a post that also said something, or linked a second clip, would lose what it said. So
 * both the list of the move and the deletion itself ask this, of the catalogue's copy and of the
 * channel's.
 */
export function isBareClipLink(text, youtubeId) {
    const links = findYoutubeLinks(text);
    return links.length === 1 && links[0]?.id === youtubeId && stripUrls(text).trim() === '';
}
/** One address → one space, plus the tail of the match that was never an address. */
function cutAddress(candidate) {
    const tail = NON_URL_TAIL_RE.exec(candidate);
    return tail === null ? ' ' : ` ${tail[0]}`;
}
/**
 * Whether `value` is a YouTube identifier: eleven characters of `[A-Za-z0-9_-]`, case kept.
 *
 * The gate for an identifier that did NOT just come out of {@link parseYoutubeId} — one read
 * back from the database, from an API answer, from a job's parameters. Task 13 puts such a
 * value into an `href` and into the address of a frame, and «it was valid when it was
 * written» is a promise about a file on disk that nothing had checked. The same expression
 * as the parser's own gate, so the two can never disagree about what an identifier is.
 */
export function isYoutubeId(value) {
    return typeof value === 'string' && YOUTUBE_ID_RE.test(value);
}
/**
 * The canonical address of a video. Every record, every oEmbed request and every «Открыть
 * в YouTube» button is built from here, so that the form exists in exactly one place.
 *
 * @throws RangeError when `id` is not {@link isYoutubeId}. Until task 13 this was a plain
 * concatenation, and `youtubeWatchUrl('"><script>…')` built a link to exactly that — the
 * same refusal `fts.ts` makes for a column name outside its list.
 */
export function youtubeWatchUrl(id) {
    return `${CANONICAL_WATCH_PREFIX}${checkedId(id)}`;
}
/**
 * Where the player frame comes from: the privacy-enhanced host (`spec.md` «Видео»). The web
 * app's page policy names the same origin in `frame-src`, and nothing else is allowed there.
 */
export const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube-nocookie.com';
/**
 * The address of the player frame for one video, built for the official IFrame Player API.
 *
 * The API documents two ways in: let it build the frame from a `<div>`, or hand it a frame
 * already pointing at `/embed/<id>?enablejsapi=1`. The second is used, so that the host is
 * written here — once, in the package both sides share — and not left to a default the API
 * keeps in its own configuration (`www.youtube.com`). The parameters:
 *
 * - `enablejsapi=1` — without it the frame ignores every command and reports no state, and
 *   neither «видео ставит музыку на паузу» nor the refusal message could work;
 * - `origin` — the page the frame may talk to. The API sends its events to this origin only;
 * - `playsinline=1` — WebKit would otherwise take a started clip to full screen at once;
 * - `rel=0` — the suggestions at the end come from the same channel, not from all of YouTube.
 *
 * @throws RangeError when `id` is not {@link isYoutubeId}, like {@link youtubeWatchUrl}.
 */
export function youtubeEmbedUrl(id, origin) {
    return (`${YOUTUBE_EMBED_ORIGIN}/embed/${checkedId(id)}` +
        `?enablejsapi=1&playsinline=1&rel=0&origin=${encodeURIComponent(origin)}`);
}
/** The identifier, or a `RangeError` naming what arrived instead. */
function checkedId(id) {
    // The expression, not `isYoutubeId`: a guard on a value already typed `string` narrows the
    // refusal branch to `never`, and the message below needs the string that arrived.
    if (YOUTUBE_ID_RE.test(id))
        return id;
    throw new RangeError(`not a YouTube video id: ${JSON.stringify(id.slice(0, 40))}`);
}
//# sourceMappingURL=youtube.js.map